import { useEffect, useState } from 'react'
import { Card, Icon } from '../atoms'
import { motion, AnimatePresence } from 'framer-motion'
import type { POICandidate } from '../../types'

export interface POIUpdateNotificationProps {
  poi: POICandidate | null
  onDismiss: () => void
}

export const POIUpdateNotification = ({
  poi,
  onDismiss,
}: POIUpdateNotificationProps) => {
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    if (poi) {
      setIsVisible(true)
      const timer = setTimeout(() => {
        setIsVisible(false)
        setTimeout(onDismiss, 300) // Wait for animation
      }, 5000) // Show for 5 seconds
      return () => clearTimeout(timer)
    }
  }, [poi, onDismiss])

  if (!poi) return null

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, y: -20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -20, scale: 0.95 }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
          className="fixed top-4 left-4 right-4 md:left-auto md:right-4 md:w-96 z-[1000]"
        >
          <Card className="bg-background-elevated border-primary/50 shadow-2xl">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                <Icon name="location" className="w-5 h-5 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm text-text mb-1">
                  New Point of Interest
                </div>
                <div className="text-base font-medium text-text mb-1 truncate">
                  {poi.title}
                </div>
                <div className="text-xs text-text-muted capitalize">
                  {poi.source === 'wiki' ? 'Wikipedia' : poi.source === 'osm' ? 'OpenStreetMap' : poi.source === 'wdnature' ? 'Wikidata' : 'Google Places'}
                </div>
              </div>
              <button
                onClick={() => {
                  setIsVisible(false)
                  setTimeout(onDismiss, 300)
                }}
                className="flex-shrink-0 text-text-muted hover:text-text transition-colors"
                aria-label="Dismiss"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </Card>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
